import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../../services/api";
import { EmployerHubLayout } from "../../components/hub/EmployerHubLayout";
import { HubEmptyPanel } from "../../components/hub/HubEmptyPanel";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Users, Video, AudioLines, FileText, ArrowRight, CalendarCheck } from "lucide-react";

const initialsOf = (name) =>
  (name || "")
    .split(" ")
    .map((w) => w[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();

const CandidateCard = ({ c, index }) => (
  <div
    className="bg-white border border-gray-200 rounded-2xl p-6 flex flex-col transition-shadow hover:shadow-md"
    data-testid={`candidate-card-${index}`}
    style={{ animation: `hubFadeUp 0.45s ease ${index * 0.08}s both` }}
  >
    <div className="flex items-start gap-4">
      {c.avatar_url ? (
        <img
          src={c.avatar_url}
          alt={c.candidate_name}
          className="h-14 w-14 rounded-full object-cover border border-gray-200"
        />
      ) : (
        <span className="h-14 w-14 rounded-full bg-blue-600 text-white text-lg font-semibold flex items-center justify-center shrink-0">
          {initialsOf(c.candidate_name)}
        </span>
      )}
      <div className="min-w-0">
        <h3 className="text-base font-semibold text-gray-900 truncate" data-testid={`candidate-card-name-${index}`}>
          {c.candidate_name}
        </h3>
        {c.professional_title && (
          <p className="text-sm text-gray-500 truncate">{c.professional_title}</p>
        )}
        {c.city_region && <p className="text-xs text-gray-400 mt-0.5">{c.city_region}</p>}
      </div>
    </div>

    {c.position_context && (
      <Badge
        variant="outline"
        className="mt-4 self-start text-blue-700 border-blue-200 bg-blue-50 font-normal"
        data-testid={`candidate-card-position-${index}`}
      >
        {c.position_context}
      </Badge>
    )}

    <div className="mt-4 space-y-1.5 text-sm text-gray-500">
      {c.has_intro && c.presentation_mode === "video" && (
        <span className="flex items-center gap-2"><Video className="h-4 w-4 text-blue-600" /> Video introduction</span>
      )}
      {c.has_intro && c.presentation_mode === "audio" && (
        <span className="flex items-center gap-2"><AudioLines className="h-4 w-4 text-blue-600" /> Audio introduction</span>
      )}
      {(c.has_resume || c.has_cover_letter) && (
        <span className="flex items-center gap-2">
          <FileText className="h-4 w-4 text-blue-600" />
          {[c.has_resume && "Resume", c.has_cover_letter && "Cover letter"].filter(Boolean).join(" · ")}
        </span>
      )}
    </div>

    <div className="mt-6 pt-4 border-t border-gray-100 flex items-center justify-between">
      {c.interview_requested_at ? (
        <span className="inline-flex items-center gap-1.5 text-xs text-emerald-700" data-testid={`candidate-card-interview-badge-${index}`}>
          <CalendarCheck className="h-3.5 w-3.5" /> Interview requested
        </span>
      ) : (
        <span />
      )}
      <Link to={`/employers/hub/candidates/${c.assignment_id}`}>
        <Button
          size="sm"
          className="bg-blue-600 hover:bg-blue-700 text-white"
          data-testid={`candidate-card-view-${index}`}
        >
          View Candidate
          <ArrowRight className="ml-1.5 h-4 w-4" />
        </Button>
      </Link>
    </div>
  </div>
);

export default function EmployerHubCandidates() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    api
      .get("/api/employer/hub/candidates")
      .then((res) => setData(res.data))
      .catch(() => setError("Unable to load candidates"));
  }, []);

  const candidates = data?.candidates || [];

  return (
    <EmployerHubLayout>
      <style>{`@keyframes hubFadeUp { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: none; } }`}</style>
      <div data-testid="employer-hub-candidates">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Candidates</h1>
        <p className="mt-2 text-gray-500">The people Inter-View has presented for your consideration.</p>

        {error && (
          <div className="mt-8 text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-4 py-3">{error}</div>
        )}

        {!data && !error && (
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="hub-candidates-loading">
            {[0, 1, 2].map((i) => (
              <div key={i} className="h-64 bg-gray-100 rounded-2xl animate-pulse" />
            ))}
          </div>
        )}

        {data && candidates.length === 0 && (
          <div className="mt-10">
            <HubEmptyPanel
              icon={Users}
              testid="hub-candidates-empty"
              title="Your candidates will be presented here"
              body="When our team has curated candidates for your service, each person will appear here with their introduction and application materials."
            />
          </div>
        )}

        {candidates.length > 0 && (
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="hub-candidates-grid">
            {candidates.map((c, i) => (
              <CandidateCard key={c.assignment_id} c={c} index={i} />
            ))}
          </div>
        )}
      </div>
    </EmployerHubLayout>
  );
}
